import React, { useState } from 'react';
import { UcatHeader } from './UcatHeader';
import { UcatFooter } from './UcatFooter';
import { ArrowLeft, Sparkles, Calculator as LucideCalculator, Flag, Clock, FileText, FormInput, AlignVerticalSpaceAround, AlignVerticalJustifyCenter, ArrowRight } from 'lucide-react';
import { Calculator } from '../Calculator/Calculator';
import { Question, AnswerType } from '../sample data/Type';
import { SplitPane, SinglePageQuestion } from '../Practice_Test/Exam_component';
import InfoIcon from '../../Assests/info_i.png';
import '../../ucat-styles.css';

interface UcatLayoutProps {
  children?: React.ReactNode;
  headerTitle: string;
  headerSubtitle?: React.ReactNode;
  showToolbar?: boolean;
  headerLeftActions?: React.ReactNode;
  headerRightActions?: React.ReactNode;
  toolbarLeftActions?: React.ReactNode;
  toolbarRightActions?: React.ReactNode;
  showExplainAnswerButton?: boolean;
  showCalculatorButton?: boolean;
  showClearFlagButton?: boolean;
  onExplainAnswerClick?: () => void;
  onCalculatorClick?: () => void;
  onClearFlagClick?: () => void;
  timerValue?: string;
  timerLow?: boolean;
  previewData?: any;
  isLoading?: boolean;
  onBack?: () => void;
  footerProps?: {
    onEndExam?: () => void;
    onNext?: () => void;
    onPrevious?: () => void;
    onNavigator?: () => void;
    onReview?: () => void;
    showNavigator?: boolean;
    showPrevious?: boolean;
    showReview?: boolean;
    nextDisabled?: boolean;
    previousDisabled?: boolean;
    leftButtonText?: string;
    rightButtonText?: string;
    reviewButtonText?: string;
    customRightActions?: React.ReactNode;
  };
}

export const UcatLayout: React.FC<UcatLayoutProps> = ({
  children,
  headerTitle,
  headerSubtitle,
  showToolbar = false,
  headerLeftActions,
  headerRightActions,
  toolbarLeftActions,
  toolbarRightActions,
  showExplainAnswerButton = false,
  showCalculatorButton = false,
  showClearFlagButton = false,
  onExplainAnswerClick,
  onCalculatorClick,
  onClearFlagClick,
  timerValue,
  timerLow,
  previewData,
  onBack,
  isLoading = false,
  footerProps
}) => {
  const [internalCalculatorOpen, setInternalCalculatorOpen] = useState(false);
  const [isExplainModalOpen, setIsExplainModalOpen] = useState(false);

  const handleCalculatorClick = () => {
    if (onCalculatorClick) {
      onCalculatorClick();
    } else {
      setInternalCalculatorOpen(true);
    }
  };

  const handleExplainAnswerClick = () => {
    if (onExplainAnswerClick) {
      onExplainAnswerClick();
    } else {
      setIsExplainModalOpen(true);
    }
  };

  // Left actions for toolbar (like Explain Answer, Calculator)
  const leftToolbarActions = (
    <div className="flex items-center h-full">
      {showExplainAnswerButton && (
        <button
          type="button"
          disabled={isLoading}
          onClick={handleExplainAnswerClick}
          className="flex items-center gap-1 text-white hover:bg-white/10 px-2 h-full transition-colors"
        >
          <Sparkles size={18} className="text-white/90" />
          <span className="text-[16px] underline decoration-1 underline-offset-4 f-tahoma">
            Explain Answer
          </span>
        </button>
      )}
      {showCalculatorButton && (
        <button
          type="button"
          onClick={handleCalculatorClick}
          className="flex items-center gap-1 text-white hover:bg-white/10 px-2 h-full transition-colors"
        >
          <LucideCalculator size={18} className="text-white/90" />
          <span className="text-[16px] font-normal">
            <span className="underline decoration-1 underline-offset-2" style={{ fontSize: '17px' }}>C</span>alculator
          </span>
        </button>
      )}
      {toolbarLeftActions}
    </div>
  );

  // Right actions for toolbar (like Flag)
  const rightToolbarActions = (
    <div className="flex items-center h-full gap-2 pr-2">
      {toolbarRightActions}
    </div>
  );

  // --- Preview Setup ---
  let previewContent = null;
  let effectiveHeaderTitle = headerTitle;
  let effectiveHeaderSubtitle = headerSubtitle;
  let effectiveHeaderRightActions = headerRightActions;
  let effectiveHeaderLeftActions = headerLeftActions;
  let effectiveFooterProps = footerProps;

  if (previewData) {
    // Map previewData to Question object
    const question: Question = {
      // ... same object structure
      question_id: 1,
      question_type_id: 0,
      question_type_name: previewData.Question_type_name || "Multiple Choice",
      question_text: previewData.question || "",
      instruction: previewData.instruction || null,
      prompt: previewData.prompt || null,
      prompt_url: previewData.prompt_url || null,
      question_url: previewData.question_url || null,
      imgWidth: previewData.imgWidth || 0,
      prompt_imgWidth: previewData.prompt_imgWidth || 0,
      source_type: "preview",
      subtype_id: null,
      subtype_name: previewData.subtype_name || null,
      answer_type: AnswerType.MCQ,
      options: (previewData.options || []).map((opt: any) => ({
        option_id: opt.id,
        option_text: opt.option || "",
        option_url: opt.option_url || null,
      })),
      explanation: previewData.explanation || null,
    };

    effectiveHeaderTitle = previewData.test_name || "Question Preview";
    effectiveHeaderSubtitle = `Question 1 of 1`;

    // Move Back button to right side of header
    if (onBack) {
      effectiveHeaderRightActions = (
        <div className="flex items-center h-full">
          {headerRightActions}
          <button
            type="button"
            disabled={isLoading}
            onClick={onBack}
            className="flex items-center gap-1.5 text-white hover:bg-white/10 px-4 h-[50px] transition-colors border-l border-white/20 ml-2 font-medium"
          >
            <ArrowLeft size={18} strokeWidth={2.5} />
            <span className='f-tahoma text-[15px]'>Back</span>
          </button>
        </div>
      );

      // Left actions should only contain toolbar buttons (Calculator/Explain)
      effectiveHeaderLeftActions = (
        <div className="flex items-center h-full">
          {leftToolbarActions}
          {headerLeftActions}
        </div>
      );
    }

    // Default footer for preview if not provided - Cosmetic Only
    if (!effectiveFooterProps) {
      effectiveFooterProps = {
        onEndExam: () => { },
        onNext: () => { },
        onPrevious: () => { },
        leftButtonText: "End Exam",
        rightButtonText: "Next",
        showPrevious: true,
        showNavigator: true,
        // showReview: true,
      };
    }

    const hasPassage = !!question.prompt || !!question.prompt_url || !!question.instruction;
    const isReadingOrVerbal = ["Reading Comprehension", "Verbal Reasoning", "Decision Making"].some(type =>
      question.question_type_name.includes(type)
    );
    const shouldUseSplitLayout = (isReadingOrVerbal || hasPassage);

    previewContent = (
      <div className="flex-1 overflow-hidden flex flex-col h-full bg-white">
        {shouldUseSplitLayout ? (
          <SplitPane
            passage={question.prompt || question.instruction || undefined}
            promptUrl={question.prompt_url || undefined}
            question={question}
            selectedOptionIndex={null}
            dragDropAnswers={{}}
            onOptionSelect={() => { }}
            onDragDropChange={() => { }}
            isReviewMode={false}
            onScrollStatusChange={() => { }}
            onLockedClick={() => { }}
          />
        ) : (
          <SinglePageQuestion
            question={question}
            selectedOptionIndex={null}
            dragDropAnswers={{}}
            onOptionSelect={() => { }}
            onDragDropChange={() => { }}
            isReviewMode={false}
            onScrollStatusChange={() => { }}
            onLockedClick={() => { }}
          />
        )}
      </div>
    );
  }

  // Content for the top-right part of the header (Timer and Counter)
  const timerContent = (timerValue || effectiveHeaderSubtitle) ? (
    <>
      {timerValue && (
        <div className={`flex items-center gap-1 text-[16px] mt-[-2px] hover-yellow transition-colors cursor-default ${timerLow ? 'text-[#FFFF00]' : 'text-white'}`}>
          <Clock size={16} strokeWidth={2.5} className="mb-0.5" />
          <span className='text-[16px] f-tahoma'>Time Remaining {timerValue}</span>
        </div>
      )}
      {effectiveHeaderSubtitle && (
        <div className="text-[16px] mt-[2px] font-[400] text-[white] flex items-center justify-end gap-1 hover-yellow transition-colors cursor-default f-tahoma">
          {typeof effectiveHeaderSubtitle === 'string' && effectiveHeaderSubtitle.trim() !== "" && <AlignVerticalSpaceAround size={18} strokeWidth={2.5} className="mb-0.5" />}
          {effectiveHeaderSubtitle}
        </div>
      )}
    </>
  ) : undefined;

  return (
    <div className="ucat-module fixed inset-0 z-[9999] flex flex-col h-screen w-screen bg-[#F5F5F5] font-inter overflow-hidden">
      {/* Header */}
      <UcatHeader
        title={effectiveHeaderTitle}
        subtitle={effectiveHeaderSubtitle}
        showToolbar={showToolbar || !!previewData}
        headerRight={timerContent}
        headerRightActions={effectiveHeaderRightActions}
        leftActions={effectiveHeaderLeftActions || leftToolbarActions}
        rightActions={rightToolbarActions}
      />

      {/* Content */}
      <div className="flex-1 overflow-hidden relative bg-white min-h-0 flex flex-col ucat-test-content">
        {previewData ? previewContent : children}
      </div>

      {/* Footer */}
      {(effectiveFooterProps) && <UcatFooter {...effectiveFooterProps} isLoading={isLoading} />}

      {/* Global Calculator Modal */}
      <Calculator
        isOpen={internalCalculatorOpen}
        onClose={() => setInternalCalculatorOpen(false)}
      />

      {/* Explain Answer Modal */}
      {isExplainModalOpen && (
        <div className="ucat-module fixed inset-0 flex items-center justify-center z-[10000] bg-black/40">
          <div className="bg-[#006DAA] w-[90%] md:w-[55%] max-w-[500px] shadow-2xl">
            <div className="text-white px-3 py-3 flex items-center justify-between f-tahoma">
              <h2 className="text-[16px] f-tahoma">Answer Explanation</h2>
            </div>
            <div className="h-[1px] bg-white w-full"></div>

            <div className="p-4 text-white f-tahoma">
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0">
                  <div className="bg-[#FFFFFF44] rounded-sm h-[32px] w-[32px]" style={{ borderRadius: "50%" }}>
                    <img src={InfoIcon} style={{ height: "100%", width: "100%" }} alt="i" />
                  </div>
                </div>
                <div className="text-[16px] leading-[1.4] mt-1">
                  <div className="text-[16px] f-tahoma max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                    {previewData?.explanation ? (
                      <div dangerouslySetInnerHTML={{ __html: previewData.explanation }} />
                    ) : (
                      <p>No explanation available for this question.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-center gap-2 mt-4">
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => setIsExplainModalOpen(false)}
                  className="px-2 py-1 bg-transparent text-white font-normal border border-white hover:bg-[#00478b] text-[16px] min-w-[50px]"
                >
                  <span className="underline text-[16px] f-tahoma">O</span>K
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
